import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import useEmblaCarousel from "embla-carousel-react";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import {
  ChevronLeft,
  ChevronRight,
  Focus,
  GalleryHorizontal,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useElementWidth } from "@/hooks/use-element-width";
import { ProductCardSkeleton } from "@/components/ProductCardSkeleton";
import useUIPreferencesStore, {
  type ProductShowcaseMode,
} from "@/store/use-ui-preferences-store";
import { getShowcaseMetrics } from "./showcase-layout";

export interface ProductShowcaseProps<T> {
  items: T[];
  getKey: (item: T, index: number) => string;
  renderItem: (item: T, index: number) => ReactNode;
  /** Üst satır, sol: başlık / açıklama */
  title?: ReactNode;
  /** Alt satır, sol: ör. "daha fazla keşfet" bağlantısı */
  action?: ReactNode;
  loading?: boolean;
  className?: string;
}

const MODES: { id: ProductShowcaseMode; icon: LucideIcon }[] = [
  { id: "strip", icon: GalleryHorizontal },
  { id: "focus", icon: Focus },
  { id: "grid", icon: LayoutGrid },
];

/** Focus modunda ortadan uzaklaşan kartın küçülme/solma eğimi (Embla örneği) */
const TWEEN_FACTOR_BASE = 0.52;
const SKELETON_COUNT = 4;
/** Ölçüm gelmeden önceki tahmin — dock genişliği; ilk kare zıplamasın */
const FALLBACK_WIDTH = 400;

/**
 * Sohbetteki ürün vitrini. Kap genişliğine göre dizilir (pencereye değil):
 * dock'ta 2 kart, tam sayfada 4-5 kart, arada sonraki kartın dilimi görünür.
 *
 * Modlar (kullanıcı tercihi, kalıcı):
 * - strip: sürükle-bırak şerit, sayfa sayfa ok/klavye
 * - focus: ortada büyük kart, komşular küçük ve soluk
 * - grid: hepsi ızgarada, kaydırma yok
 */
export function ProductShowcase<T>({
  items,
  getKey,
  renderItem,
  title,
  action,
  loading = false,
  className,
}: ProductShowcaseProps<T>) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";
  const mode = useUIPreferencesStore((s) => s.productShowcaseMode);
  const setMode = useUIPreferencesStore((s) => s.setProductShowcaseMode);

  // ── Kap genişliği ──
  const rootRef = useRef<HTMLDivElement>(null);
  const width = useElementWidth(rootRef);

  const metrics = useMemo(
    () => getShowcaseMetrics(width || FALLBACK_WIDTH, mode, items.length),
    [width, mode, items.length],
  );
  const trackStyle = {
    "--card-w": `${metrics.cardWidth}px`,
    "--gap": `${metrics.gap}px`,
  } as CSSProperties;

  // ── Embla ──
  const scrollable = mode !== "grid";
  const emblaOptions = useMemo<EmblaOptionsType>(
    () =>
      mode === "focus"
        ? {
            align: "center",
            containScroll: false,
            slidesToScroll: 1,
            direction: isRtl ? "rtl" : "ltr",
          }
        : {
            align: "start",
            containScroll: "trimSnaps",
            slidesToScroll: "auto",
            direction: isRtl ? "rtl" : "ltr",
          },
    [mode, isRtl],
  );
  const [viewportRef, embla] = useEmblaCarousel(emblaOptions);

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [selected, setSelected] = useState(0);
  const [snapCount, setSnapCount] = useState(0);
  const progressRef = useRef<HTMLDivElement>(null);

  const onSelect = useCallback((api: EmblaCarouselType) => {
    setCanPrev(api.canScrollPrev());
    setCanNext(api.canScrollNext());
    setSelected(api.selectedScrollSnap());
    setSnapCount(api.scrollSnapList().length);
  }, []);

  // İlerleme çubuğu: her scroll karesinde state yerine doğrudan DOM
  const onScroll = useCallback((api: EmblaCarouselType) => {
    const thumb = progressRef.current;
    if (!thumb) return;
    const n = api.scrollSnapList().length;
    const p = Math.min(1, Math.max(0, api.scrollProgress()));
    const w = Math.max(100 / Math.max(n, 1), 18);
    thumb.style.width = `${w}%`;
    thumb.style.insetInlineStart = `${p * (100 - w)}%`;
  }, []);

  useEffect(() => {
    if (!embla) return;
    onSelect(embla);
    onScroll(embla);
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    embla.on("scroll", onScroll);
    embla.on("reInit", onScroll);
    return () => {
      embla.off("select", onSelect);
      embla.off("reInit", onSelect);
      embla.off("scroll", onScroll);
      embla.off("reInit", onScroll);
    };
  }, [embla, onSelect, onScroll]);

  // Kart ölçüsü değişince Embla snap noktalarını yeniden hesaplasın
  useEffect(() => {
    embla?.reInit();
  }, [embla, metrics.cardWidth, metrics.gap, items.length]);

  // Focus modu: ortadan uzaklığa göre ölçek/opaklık (iç sarmalayıcıya, slot değil)
  useEffect(() => {
    if (!embla || mode !== "focus") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const tween = (api: EmblaCarouselType) => {
      const progress = api.scrollProgress();
      const snaps = api.scrollSnapList();
      const factor = TWEEN_FACTOR_BASE * snaps.length;
      api.slideNodes().forEach((slide, i) => {
        const node = slide.firstElementChild as HTMLElement | null;
        if (!node) return;
        const diff = (snaps[i] ?? 0) - progress;
        const tv = Math.max(0, 1 - Math.abs(diff * factor));
        node.style.transform = `scale(${0.86 + 0.14 * tv})`;
        node.style.opacity = `${0.5 + 0.5 * tv}`;
      });
    };
    tween(embla);
    embla.on("scroll", tween);
    embla.on("reInit", tween);
    return () => {
      embla.off("scroll", tween);
      embla.off("reInit", tween);
      embla.slideNodes().forEach((slide) => {
        const node = slide.firstElementChild as HTMLElement | null;
        if (node) {
          node.style.transform = "";
          node.style.opacity = "";
        }
      });
    };
  }, [embla, mode]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!embla) return;
    const prevKey = isRtl ? "ArrowRight" : "ArrowLeft";
    const nextKey = isRtl ? "ArrowLeft" : "ArrowRight";
    if (e.key === prevKey) embla.scrollPrev();
    else if (e.key === nextKey) embla.scrollNext();
    else if (e.key === "Home") embla.scrollTo(0);
    else if (e.key === "End") embla.scrollTo(embla.scrollSnapList().length - 1);
    else return;
    e.preventDefault();
  };

  const showModes = !loading && items.length > 2;
  const hasPages = scrollable && snapCount > 1;

  // ── İçerik ──
  let body: ReactNode;
  if (loading) {
    body = (
      <div className="flex overflow-hidden" style={trackStyle}>
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <div
            key={i}
            className="w-[var(--card-w)] shrink-0 pe-[var(--gap)] last:pe-0"
          >
            <ProductCardSkeleton />
          </div>
        ))}
      </div>
    );
  } else if (mode === "grid") {
    body = (
      <div
        className="grid"
        style={{
          gap: metrics.gap,
          gridTemplateColumns: `repeat(auto-fill, minmax(${metrics.gridMin}px, 1fr))`,
        }}
      >
        {items.map((item, i) => (
          <div
            key={getKey(item, i)}
            className="showcase-enter min-w-0"
            style={{ "--i": i } as CSSProperties}
          >
            {renderItem(item, i)}
          </div>
        ))}
      </div>
    );
  } else {
    body = (
      <div className="group/showcase relative">
        <div
          ref={viewportRef}
          role="region"
          aria-roledescription="carousel"
          tabIndex={0}
          onKeyDown={onKeyDown}
          data-fade-start={canPrev || undefined}
          data-fade-end={canNext || undefined}
          className={cn(
            "showcase-viewport -my-2 overflow-hidden py-2 outline-none",
            "focus-visible:ring-2 focus-visible:ring-module-accent/50 rounded-xl",
          )}
        >
          <div
            className="flex touch-pan-y items-stretch ms-[calc(var(--gap)*-1)]"
            style={trackStyle}
          >
            {items.map((item, i) => {
              const isActive = mode === "focus" && i === selected;
              return (
                <div
                  key={getKey(item, i)}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={t("chat.productShowcase.position", {
                    current: i + 1,
                    total: items.length,
                  })}
                  className="showcase-enter min-w-0 shrink-0 grow-0 basis-[calc(var(--card-w)+var(--gap))] ps-[var(--gap)]"
                  style={{ "--i": i } as CSSProperties}
                  // Focus modunda kenardaki karta tıklamak onu ortaya getirir
                  onClickCapture={
                    mode === "focus" && !isActive
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          embla?.scrollTo(i);
                        }
                      : undefined
                  }
                >
                  <div
                    className={cn(
                      "h-full will-change-transform",
                      mode === "focus" && "transition-[filter] duration-300",
                      mode === "focus" && !isActive && "cursor-pointer",
                    )}
                  >
                    {renderItem(item, i)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Kenar okları: masaüstünde hover/odakta belirir, dokunmatikte yok */}
        {hasPages && (
          <>
            <EdgeButton
              side="start"
              disabled={!canPrev}
              onClick={() => embla?.scrollPrev()}
              label={t("common.previous")}
            >
              {isRtl ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
            </EdgeButton>
            <EdgeButton
              side="end"
              disabled={!canNext}
              onClick={() => embla?.scrollNext()}
              label={t("common.next")}
            >
              {isRtl ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
            </EdgeButton>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      className={cn("flex w-full min-w-0 flex-col gap-3", className)}
    >
      {(title || showModes) && (
        <div className="flex items-center justify-between gap-3 px-1">
          <div className="min-w-0 flex-1 text-xs font-medium text-text-secondary dark:text-gray-400 sm:text-sm">
            {title}
          </div>
          {showModes && (
            <div
              role="radiogroup"
              aria-label={t("chat.productShowcase.layout")}
              className="flex shrink-0 items-center gap-0.5 rounded-lg border border-border-secondary bg-white p-0.5 dark:border-border-primary dark:bg-bg-secondary"
            >
              {MODES.map(({ id, icon: Icon }) => {
                const active = id === mode;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    aria-label={t(`chat.productShowcase.${id}`)}
                    title={t(`chat.productShowcase.${id}`)}
                    onClick={() => setMode(id)}
                    className={cn(
                      "flex h-7 w-7 items-center justify-center rounded-md transition-colors",
                      active
                        ? "bg-module-accent-soft text-module-accent"
                        : "text-text-tertiary hover:bg-bg-secondary hover:text-text-secondary dark:hover:bg-gray-700",
                    )}
                  >
                    <Icon size={15} strokeWidth={2} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {body}

      {(action || hasPages) && (
        <div className="flex flex-wrap items-center gap-3 px-1">
          {action}
          {hasPages && (
            <div className="ms-auto flex items-center gap-3">
              <span className="text-xs tabular-nums text-text-tertiary dark:text-gray-500">
                {t("chat.productShowcase.position", {
                  current: selected + 1,
                  total: snapCount,
                })}
              </span>
              {/* Kaydırma ilerlemesi — thumb genişliği sayfa payı, konumu progress */}
              <div
                aria-hidden
                className="relative h-1 w-16 overflow-hidden rounded-full bg-border-secondary dark:bg-gray-700"
              >
                <div
                  ref={progressRef}
                  className="absolute inset-y-0 rounded-full bg-module-accent transition-[inset-inline-start,width] duration-150 ease-out"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EdgeButton({
  side,
  disabled,
  onClick,
  label,
  children,
}: {
  side: "start" | "end";
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "absolute top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full",
        "border border-border-secondary bg-white/90 text-text-secondary shadow-md backdrop-blur",
        "dark:border-border-primary dark:bg-gray-900/80 dark:text-gray-200",
        "transition-[opacity,transform] duration-200 hover:scale-105 hover:bg-white dark:hover:bg-gray-900",
        "opacity-0 group-hover/showcase:opacity-100 focus-visible:opacity-100 disabled:!opacity-0",
        "[@media(hover:hover)]:flex",
        side === "start" ? "start-1" : "end-1",
      )}
    >
      {children}
    </button>
  );
}
